import { Link, Skeleton, Stack, Typography } from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { ArrowNarrowRight, Share05 } from "@untitled-ui/icons-react"

import { useEffect, useState } from "react"

import { SwapOrderDetails } from "./SwapOrderDetails"
import { DcaOrderFormValues } from "./types/DcaOrderForm"
import { LimitOrderFormValues } from "./types/LimitOrderForm"
import { StopLosOrderFormValues } from "./types/StopLossOrderForm"
import { OrderType } from "./types/swap-types"
import { getDcaParams } from "../../ao/agents-api"
import { useAo } from "../../ao/ao"
import { TokenInfo } from "../../ao/token"
import { NumberFont, TitleFont } from "../../theme"
import {
  parseBigIntAsNumber,
  formatTicker,
  formatNumberAuto,
  formatDuration,
} from "../../utils/format"
import { SuccessIcon } from "../SuccessIcon"
import TokenLogo from "../TokenLogo"
import { TokenPill } from "../TokenPill"
import { CreateAgentAnimation, CreateAgentStage } from "../create-agent-anim/CreateAgentAnimation"

type SwapOrderSuccessFormProps = {
  poolId: string
  orderType: OrderType
  orderExtra: LimitOrderFormValues | DcaOrderFormValues | StopLosOrderFormValues | null
  orderOutputAmount: bigint
  orderConfirmationId: string
  fromToken: TokenInfo
  toToken: TokenInfo
  fromValue: string
  toValue: string
  orderId: string
}

const SUCCESS_MSG: Record<OrderType, string> = {
  swap: "Successful Swap",
  limit: "Limit order placed",
  "stop-loss": "Stop-loss order placed",
  dca: "DCA order placed",
}

export function SwapOrderSuccessForm(props: SwapOrderSuccessFormProps) {
  const {
    orderType,
    orderOutputAmount,
    orderConfirmationId,
    orderId,
    fromValue,
    fromToken,
    toToken,
    toValue,
    poolId,
    orderExtra,
  } = props

  const [stage, setStage] = useState<CreateAgentStage>("spawning")

  // hack to get the initial states in the animation
  useEffect(() => {
    setTimeout(() => {
      setStage("done")
    }, 0)
  }, [])

  const hasOutput =
    orderType === "swap" ||
    orderType === "limit" ||
    (orderType === "stop-loss" && (orderExtra as StopLosOrderFormValues)?.variant === "limit")

  const ao = useAo()

  const { data: dcaParams, isLoading } = useQuery({
    queryKey: ["dcaParams", orderExtra],
    queryFn: async () => {
      const result = await getDcaParams((orderExtra as DcaOrderFormValues)?.runningTime, ao)
      console.log("onDcaParams", result)
      return result
    },
    enabled: orderType === "dca",
  })

  return (
    <>
      {orderType !== "swap" && (
        <Stack alignItems="center">
          <CreateAgentAnimation stage={stage} />
        </Stack>
      )}
      <Stack gap={2} alignItems="center">
        {orderType !== "swap" ? (
          <SuccessIcon
            width={50}
            height={50}
            //
          />
        ) : (
          <SuccessIcon />
        )}
        <Typography variant="h5" fontFamily={TitleFont} data-testid="successMessage">
          {SUCCESS_MSG[orderType]}
        </Typography>
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          sx={{
            width: "100%",
            "& > *": {
              flex: 1,
              flexShrink: 0,
              overflow: "hidden",
            },
            "& > *:nth-child(1)": {
              flexBasis: "calc(50% - 12px - var(--spacing))",
              alignItems: "flex-end",
            },
            "& > *:nth-child(2)": {
              flexBasis: "24px",
            },
            "& > *:nth-child(3)": {
              flexBasis: "calc(50% - 12px - var(--spacing))",
            },
          }}
        >
          <Stack>
            <TokenPill variant="outlined">
              <TokenLogo tx={fromToken.logo} alt={`${fromToken.name} logo`} size="small" />
              <Typography
                fontFamily={NumberFont}
                sx={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                }}
              >
                {formatNumberAuto(fromValue)}
              </Typography>
              {formatTicker(fromToken.ticker)}
            </TokenPill>
          </Stack>
          <ArrowNarrowRight />
          <Stack>
            <TokenPill variant="outlined">
              <TokenLogo tx={toToken.logo} alt={`${toToken.name} logo`} size="small" />
              {hasOutput && (
                <Typography
                  fontFamily={NumberFont}
                  sx={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {formatNumberAuto(
                    orderType === "swap"
                      ? parseBigIntAsNumber(orderOutputAmount, toToken.denomination)
                      : toValue,
                  )}
                </Typography>
              )}
              {formatTicker(toToken.ticker)}
              {!hasOutput && orderType !== "dca" && <> at market price</>}
            </TokenPill>
          </Stack>
        </Stack>
        {orderType === "dca" && (
          <>
            Dollar Cost Averaging {isLoading ? <Skeleton width={60} /> : dcaParams?.totalTicks}{" "}
            times, every{" "}
            {isLoading ? (
              <Skeleton width={60} />
            ) : (
              formatDuration(dcaParams?.tickInterval, "seconds")
            )}
            .
          </>
        )}
        <Stack>
          <SwapOrderDetails
            orderType={orderType}
            orderExtra={orderExtra}
            // price={null}
            // feePercentageBps={feePercentageBps}
            // receiveAtLeast={receiveAtLeast}
            // slippage={slippage}
            fromToken={fromToken as TokenInfo}
            toToken={toToken as TokenInfo}
            // price={price}
            // priceImpact={priceImpact}
            // fee={fee}
          />
        </Stack>
        <Link
          href={`https://ao.link/#/message/${orderType === "swap" ? orderConfirmationId : orderId}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            gap: 0.5,
            display: "flex",
            alignItems: "center",
            flexDirection: "row",

            "& svg": {
              width: "1rem",
              height: "1rem",
            },
          }}
          color="secondary"
          data-testid="viewOnAoLink"
        >
          View on ao.link
          <Share05 />
        </Link>
        <Link
          href={
            orderType === "swap"
              ? `https://dexi.arweave.net/#/pool/${poolId}?swapId=${orderConfirmationId}`
              : `https://dexi.arweave.net/#/entity/${orderId}`
          }
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            marginBottom: 2,
            gap: 0.5,
            display: "flex",
            alignItems: "center",
            flexDirection: "row",

            "& svg": {
              width: "1rem",
              height: "1rem",
            },
          }}
          color="secondary"
          data-testid="viewOnDexi"
        >
          View on Dexi
          <Share05 />
        </Link>
      </Stack>
    </>
  )
}
